import {
  isGeomKind,
  isShapeObjectType,
  type GeomKind,
  type ShapeObjectType,
} from "@bezpart-board/shared";
import type { SceneObject } from "../../SceneHost";
import {
  diamondPathD,
  geomPathD,
  starPathD,
  trianglePathD,
} from "../../geometry/shapePaths";
import {
  geomVolumeLayers,
  isVolumeGeomKind,
  type GeomDrawLayer,
} from "../../geometry/volumeShapes";
import {
  shapeCenterPoint,
  shapeCenterRadius,
  shapeSupportsCenter,
} from "../../geometry/shapeCenter";
import { strokeDasharrayForStyle } from "../../../lib/board/sceneStyle";
import { svgGroupTransform } from "../../transform/rotate";
import {
  applyShapePaint,
  shapeFill,
  shapeFillOpacity,
} from "./shapePaint";
import {
  pathLinejoin,
  pathStrokeColor,
  pathStrokeDash,
  pathStrokeWidth,
  readObjectStyle,
} from "../../../lib/board/sceneStyle";

const SVG_NS = "http://www.w3.org/2000/svg";

export function isShapeObject(
  obj: SceneObject
): obj is SceneObject & { type: ShapeObjectType; w: number; h: number } {
  return isShapeObjectType(obj.type);
}

function syncRect(el: SVGRectElement, w: number, h: number, cornerRadius?: number) {
  el.setAttribute("x", "0");
  el.setAttribute("y", "0");
  el.setAttribute("width", String(w));
  el.setAttribute("height", String(h));
  if (typeof cornerRadius === "number" && cornerRadius > 0) {
    el.setAttribute("rx", String(cornerRadius));
    el.setAttribute("ry", String(cornerRadius));
  } else {
    el.removeAttribute("rx");
    el.removeAttribute("ry");
  }
}

function syncEllipse(el: SVGEllipseElement, w: number, h: number) {
  el.setAttribute("cx", String(w / 2));
  el.setAttribute("cy", String(h / 2));
  el.setAttribute("rx", String(w / 2));
  el.setAttribute("ry", String(h / 2));
}

function clearShapeChildren(g: SVGGElement) {
  while (g.firstChild) g.removeChild(g.firstChild);
}

function pathForShape(obj: SceneObject & { type: ShapeObjectType; w: number; h: number }): string {
  const w = obj.w;
  const h = obj.h;
  if (obj.type === "triangle") return trianglePathD(w, h);
  if (obj.type === "diamond") return diamondPathD(w, h);
  if (obj.type === "star") {
    const pts = typeof obj.starPoints === "number" ? obj.starPoints : 5;
    return starPathD(w, h, pts);
  }
  if (obj.type === "geom" && isGeomKind(obj.kind)) {
    return geomPathD(obj.kind as GeomKind, w, h);
  }
  return diamondPathD(w, h);
}

function hiddenDasharray(strokeWidth: number): string {
  const w = Math.max(1, strokeWidth);
  return `${w * 2.2} ${w * 2.2}`;
}

function applyVolumeLayer(el: SVGPathElement, layer: GeomDrawLayer, obj: SceneObject): void {
  // Style helpers принимают StyleCarrier; SceneObject совместим по полям style.
  const styled = obj as SceneObject & { style?: Record<string, unknown> };
  el.setAttribute("d", layer.d);
  el.setAttribute("stroke-linejoin", pathLinejoin(styled as never));
  el.setAttribute("stroke-linecap", "round");
  const strokeWidth = pathStrokeWidth(styled as never);
  const stroke = pathStrokeColor(styled as never);

  if (layer.paint === "fill") {
    el.setAttribute("fill", shapeFill(obj));
    el.setAttribute("stroke", "none");
    el.removeAttribute("stroke-dasharray");
    const fillOpacity = shapeFillOpacity(obj);
    if (typeof fillOpacity === "number") {
      el.setAttribute("fill-opacity", String(fillOpacity));
    } else {
      el.removeAttribute("fill-opacity");
    }
    return;
  }

  el.setAttribute("fill", "none");
  el.setAttribute("stroke", stroke);
  el.setAttribute("stroke-width", String(strokeWidth));
  const strokeOpacity = readObjectStyle(styled as never).strokeOpacity;
  if (typeof strokeOpacity === "number") {
    el.setAttribute("stroke-opacity", String(strokeOpacity));
  } else {
    el.removeAttribute("stroke-opacity");
  }

  if (layer.paint === "dashed") {
    el.setAttribute("stroke-dasharray", hiddenDasharray(strokeWidth));
    return;
  }

  const dash = pathStrokeDash(styled as never);
  const arr = strokeDasharrayForStyle(dash === "wavy" ? "solid" : dash, strokeWidth);
  if (arr) el.setAttribute("stroke-dasharray", arr);
  else el.removeAttribute("stroke-dasharray");
}

function syncVolumeGeom(g: SVGGElement, obj: SceneObject, kind: GeomKind, w: number, h: number): void {
  const layers = geomVolumeLayers(kind, w, h);
  if (!layers) return;
  clearShapeChildren(g);
  for (const layer of layers) {
    const el = document.createElementNS(SVG_NS, "path");
    el.setAttribute("class", `wb-geom-layer wb-geom-layer--${layer.paint}`);
    applyVolumeLayer(el, layer, obj);
    g.appendChild(el);
  }
}

/** Точка в центре плоской фигуры; цвет — как у контура, чтобы не спорила с заливкой. */
function appendCenterDot(g: SVGGElement, obj: SceneObject, w: number, h: number): void {
  if (obj.showCenter !== true) return;
  if (!shapeSupportsCenter(obj as { type: string; kind?: unknown })) return;
  const styled = obj as SceneObject & { style?: Record<string, unknown> };
  const { x, y } = shapeCenterPoint(obj as { type: string; kind?: unknown }, w, h);
  const dot = document.createElementNS(SVG_NS, "circle");
  dot.setAttribute("class", "wb-shape-center");
  dot.setAttribute("cx", String(x));
  dot.setAttribute("cy", String(y));
  dot.setAttribute("r", String(shapeCenterRadius(pathStrokeWidth(styled as never))));
  dot.setAttribute("fill", pathStrokeColor(styled as never));
  dot.setAttribute("stroke", "none");
  dot.setAttribute("pointer-events", "none");
  g.appendChild(dot);
}

export function syncShapeOnGroup(g: SVGGElement, obj: SceneObject): void {
  if (!isShapeObject(obj)) return;
  const w = typeof obj.w === "number" ? obj.w : 100;
  const h = typeof obj.h === "number" ? obj.h : 100;
  g.setAttribute("transform", svgGroupTransform(obj));

  const type = obj.type;
  if (type === "rect") {
    clearShapeChildren(g);
    const el = document.createElementNS(SVG_NS, "rect");
    g.appendChild(el);
    syncRect(el, w, h, typeof obj.cornerRadius === "number" ? obj.cornerRadius : undefined);
    applyShapePaint(el, obj);
    appendCenterDot(g, obj, w, h);
    return;
  }

  if (type === "ellipse") {
    clearShapeChildren(g);
    const el = document.createElementNS(SVG_NS, "ellipse");
    g.appendChild(el);
    syncEllipse(el, w, h);
    applyShapePaint(el, obj);
    appendCenterDot(g, obj, w, h);
    return;
  }

  if (type === "geom" && isGeomKind(obj.kind) && isVolumeGeomKind(obj.kind)) {
    syncVolumeGeom(g, obj, obj.kind, w, h);
    return;
  }

  clearShapeChildren(g);
  const pathEl = document.createElementNS(SVG_NS, "path");
  g.appendChild(pathEl);
  if (type === "geom" && obj.kind === "ring") {
    pathEl.setAttribute("fill-rule", "evenodd");
  }
  pathEl.setAttribute("d", pathForShape({ ...obj, w, h }));
  applyShapePaint(pathEl, obj);
  appendCenterDot(g, obj, w, h);
}

export function renderShapeObject(obj: SceneObject): SVGGElement | null {
  if (!isShapeObject(obj)) return null;
  const g = document.createElementNS(SVG_NS, "g");
  g.setAttribute("class", `wb-scene-obj wb-scene-obj--${obj.type}`);
  g.setAttribute("data-object-id", obj.id);
  syncShapeOnGroup(g, obj);
  return g;
}
